'use client';

/**
 * useDashboardPreferences
 * Story 107-10: UserPreferences DB migration (Sally Tier 1 UX finding)
 *
 * MIGRATION STRATEGY (localStorage → DB):
 *   Első belépéskor ha localStorage-ban van 'kgc-dashboard-preferences' érték
 *   és DB-ben még nincs adat, automatikusan feltölti DB-be (egyszer), majd
 *   localStorage key-t törli. Ez garantálja Zsuzsi régi config-jának megőrzését.
 *
 * FALLBACK:
 *   Ha az API nem elérhető (offline, 5xx), local state-re esik vissza — UX continuity.
 *   Auth store logout: queryClient.clear() + localStorage törlés nem érinti a DB-t.
 *   Következő login = új JWT = fresh /me/preferences fetch.
 */

import {
  bulkSetPreferences,
  deletePreferences,
  getPreferences,
  setPreference,
} from '@/api/user-preferences';
import { useAuth } from '@/hooks/useAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  DashboardPreset,
  RoleCode,
  SectionId,
  UserDashboardPreferences,
} from './dashboard-types';
import { DASHBOARD_PRESETS, DEFAULT_SECTION_ORDER } from './dashboard-types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Legacy localStorage kulcs — migráció forrása, törlés után nincs több írás */
const LEGACY_STORAGE_KEY = 'kgc-dashboard-preferences';
/** React Query query key */
const QUERY_KEY = ['user-preferences', 'dashboard-widgets'] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map legacy UserRole to RoleCode
 * TODO: Replace when Auth Epic provides proper role mapping
 */
function mapLegacyRole(legacyRole: string | null): RoleCode {
  const mapping: Record<string, RoleCode> = {
    ADMIN: 'ROLE_ADMIN',
    STORE_MANAGER: 'ROLE_MANAGER',
    OPERATOR: 'ROLE_STOCK',
  };
  // QA-100: `null` = feloldatlan szerep -> a legszűkebb preset (a korábbi
  // `?? 'ROLE_STOCK'` fallback ugyanez, csak most a null-eset is kimondva).
  if (legacyRole === null) return 'ROLE_STOCK';

  return mapping[legacyRole] ?? 'ROLE_STOCK';
}

/**
 * Get default preset for a role
 */
function getPresetForRole(roleCode: RoleCode): DashboardPreset {
  const preset = DASHBOARD_PRESETS.find(p => p.roleCode === roleCode);
  return (
    preset ?? {
      roleCode,
      expandedSections: ['finance'],
      pinnedWidgets: [],
      defaultRefreshInterval: 300,
    }
  );
}

/**
 * Migrate analytics→rental (Epic 74)
 */
function migrateAnalyticsToRental(prefs: UserDashboardPreferences): UserDashboardPreferences {
  const migrateSectionIds = (ids: SectionId[] | undefined): SectionId[] | undefined => {
    if (!ids) return ids;
    return ids.map(id => (id === ('analytics' as SectionId) ? 'rental' : id)) as SectionId[];
  };

  return {
    ...prefs,
    expandedSections: migrateSectionIds(prefs.expandedSections) ?? [],
    pinnedSections: migrateSectionIds(prefs.pinnedSections) ?? [],
    collapsedSections: migrateSectionIds(prefs.collapsedSections) ?? [],
    sectionOrder: migrateSectionIds(prefs.sectionOrder),
  };
}

/**
 * Parse DB entries → UserDashboardPreferences
 */
function parseDbEntries(
  entries: Array<{ key: string; value: unknown }>
): UserDashboardPreferences | null {
  if (entries.length === 0) return null;

  // B-C4 FIX (code review 2026-04-16): Prototype pollution guard.
  // DB row-ban key='__proto__' / 'constructor' / 'prototype' tárolható (a unique
  // index csak [userId, tenantId, category, key] összeset kényszeríti), ezért
  // támadó saját preference rekordjában Object.prototype szennyezést indíthatna.
  const PROTO_POLLUTION_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
  const result: Partial<UserDashboardPreferences> = {};
  for (const { key, value } of entries) {
    if (PROTO_POLLUTION_KEYS.has(key)) continue;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (result as any)[key] = value;
  }

  // Ensure required fields exist
  if (!result.expandedSections) result.expandedSections = [];
  if (!result.pinnedWidgets) result.pinnedWidgets = [];
  if (!result.pinnedSections) result.pinnedSections = [];
  if (!result.collapsedSections) result.collapsedSections = [];

  return migrateAnalyticsToRental(result as UserDashboardPreferences);
}

/**
 * Serialize UserDashboardPreferences → key/value entries for bulk PUT
 */
function prefsToEntries(prefs: UserDashboardPreferences): Array<{ key: string; value: unknown }> {
  return [
    { key: 'expandedSections', value: prefs.expandedSections },
    { key: 'pinnedWidgets', value: prefs.pinnedWidgets },
    { key: 'pinnedSections', value: prefs.pinnedSections },
    { key: 'collapsedSections', value: prefs.collapsedSections },
    ...(prefs.sectionOrder ? [{ key: 'sectionOrder', value: prefs.sectionOrder }] : []),
  ];
}

// ---------------------------------------------------------------------------
// Return type (same public API as before — no caller changes needed)
// ---------------------------------------------------------------------------

export interface UseDashboardPreferencesReturn {
  expandedSections: SectionId[];
  pinnedWidgets: string[];
  pinnedSections: SectionId[];
  sectionOrder: SectionId[];
  isLoading: boolean;
  toggleSection: (sectionId: SectionId) => void;
  setExpandedSections: (sectionIds: SectionId[]) => void;
  togglePinWidget: (widgetId: string) => void;
  togglePinSection: (sectionId: SectionId) => void;
  moveSection: (sectionId: SectionId, direction: 'up' | 'down') => void;
  resetToDefaults: () => void;
  isSectionExpanded: (sectionId: SectionId) => boolean;
  isWidgetPinned: (widgetId: string) => boolean;
  isSectionPinned: (sectionId: SectionId) => boolean;
  canMoveUp: (sectionId: SectionId) => boolean;
  canMoveDown: (sectionId: SectionId) => boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Dashboard preferences hook — DB-backed (Story 107-10)
 *
 * Replaces localStorage persistence with /api/v1/me/preferences/dashboard-widgets.
 * Public API unchanged — no DashboardPage.tsx refactor needed.
 *
 * @example
 * ```tsx
 * const { expandedSections, toggleSection, isSectionExpanded } = useDashboardPreferences();
 * ```
 */
export function useDashboardPreferences(): UseDashboardPreferencesReturn {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const migrationDoneRef = useRef(false);

  // Local fallback state (offline / API unavailable)
  const [localPrefs, setLocalPrefs] = useState<UserDashboardPreferences | null>(null);

  // Role preset
  const rolePreset = useMemo(() => {
    if (!user) return null;
    const roleCode = mapLegacyRole(user.role);
    return getPresetForRole(roleCode);
  }, [user]);

  // ---------------------------------------------------------------------------
  // DB fetch via React Query
  // ---------------------------------------------------------------------------

  const { data: dbData, isLoading: dbLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await getPreferences('dashboard-widgets');
      return res.data;
    },
    enabled: !authLoading && !!user,
    staleTime: 5 * 60 * 1000, // 5 perc — dashboard pref ritkán változik
    retry: 1, // 1 retry, utána fallback local state
  });

  // ---------------------------------------------------------------------------
  // localStorage → DB automatikus migráció (egyszer, első login)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (authLoading || !user || dbLoading) return;
    if (migrationDoneRef.current) return;

    migrationDoneRef.current = true;

    // Ha DB már tartalmaz adatot, nincs mit migrálni
    if (dbData && dbData.length > 0) return;

    // Ellenőrzés: van-e legacy localStorage adat
    try {
      const stored = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!stored) return;

      const parsed = JSON.parse(stored) as UserDashboardPreferences;
      const migrated = migrateAnalyticsToRental(parsed);
      const entries = prefsToEntries(migrated);

      // Upload DB-be aszinkron (fire-and-forget, hiba esetén silent)
      bulkSetPreferences('dashboard-widgets', entries)
        .then(() => {
          // Siker: localStorage kulcs törlése, React Query invalidáció
          localStorage.removeItem(LEGACY_STORAGE_KEY);
          void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        })
        .catch((err: unknown) => {
          // Nem töröljük a localStorage-t ha az upload sikertelen — retry next login
          console.warn('[useDashboardPreferences] localStorage→DB migration failed:', err);
        });
    } catch {
      // JSON parse hiba — corrupted localStorage, töröljük
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  }, [authLoading, user, dbLoading, dbData, queryClient]);

  // ---------------------------------------------------------------------------
  // Mutation (optimistic update)
  // ---------------------------------------------------------------------------

  const { mutate: persistPrefs } = useMutation({
    mutationFn: async (prefs: UserDashboardPreferences) => {
      const entries = prefsToEntries(prefs);
      await bulkSetPreferences('dashboard-widgets', entries);
    },
    onSuccess: (_data, prefs) => {
      // Confirmed write: push the written value into the query cache and clear the
      // optimistic override, so dbData leads again and a later cross-tab refetch can
      // win (eventual cross-tab consistency) — with no revert window. Guarded by
      // reference equality so a rapid second toggle (newer localPrefs) isn't dropped
      // before its own write confirms.
      queryClient.setQueryData(QUERY_KEY, prefsToEntries(prefs));
      setLocalPrefs(prev => (prev === prefs ? null : prev));
    },
    // No onSettled refetch: a blanket refetch would pull the just-applied edit back
    // (the Story 107-10 regression). Failure rollback is handled per-call in
    // updatePrefs, which captures the pre-edit localPrefs snapshot in closure.
  });

  // ---------------------------------------------------------------------------
  // Single-key mutation (for incremental changes)
  // ---------------------------------------------------------------------------

  const { mutate: persistKey } = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      await setPreference('dashboard-widgets', key, value);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  // ---------------------------------------------------------------------------
  // Computed preferences (DB → local state fallback)
  // ---------------------------------------------------------------------------

  const preferences = useMemo<UserDashboardPreferences | null>(() => {
    // localPrefs holds the latest optimistic edit and WINS — so a toggle reflects
    // synchronously and a background DB write/refetch can't revert it. Story 107-10
    // regressed this when persistence moved to the DB-backed React Query (dbData was
    // read first, swallowing the synchronous optimistic update).
    if (localPrefs) return localPrefs;
    if (dbData && dbData.length > 0) {
      return parseDbEntries(dbData);
    }
    return null;
  }, [dbData, localPrefs]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const expandedSections = useMemo(() => {
    return preferences?.expandedSections ?? rolePreset?.expandedSections ?? [];
  }, [preferences, rolePreset]);

  const pinnedWidgets = useMemo(() => {
    return preferences?.pinnedWidgets ?? rolePreset?.pinnedWidgets ?? [];
  }, [preferences, rolePreset]);

  const pinnedSections = useMemo(() => {
    return preferences?.pinnedSections ?? [];
  }, [preferences]);

  const sectionOrder = useMemo(() => {
    return preferences?.sectionOrder ?? DEFAULT_SECTION_ORDER;
  }, [preferences]);

  // ---------------------------------------------------------------------------
  // Updater helper — updates local + DB
  // ---------------------------------------------------------------------------

  const updatePrefs = useCallback(
    (updater: (prev: UserDashboardPreferences) => UserDashboardPreferences) => {
      const current = preferences ?? {
        expandedSections: rolePreset?.expandedSections ?? [],
        pinnedWidgets: rolePreset?.pinnedWidgets ?? [],
        pinnedSections: [],
        collapsedSections: [],
      };
      const snapshot = localPrefs; // rollback target (null → dbData / role-preset re-wins)
      const next = updater(current);
      setLocalPrefs(next); // synchronous optimistic update — wins over dbData in `preferences`
      persistPrefs(next, {
        // Roll the optimistic local state back if the DB write fails.
        onError: () => setLocalPrefs(snapshot),
      }); // async DB write
    },
    [preferences, localPrefs, rolePreset, persistPrefs]
  );

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const toggleSection = useCallback(
    (sectionId: SectionId) => {
      updatePrefs(current => {
        const isExpanded = current.expandedSections.includes(sectionId);
        return {
          ...current,
          expandedSections: isExpanded
            ? current.expandedSections.filter(id => id !== sectionId)
            : [...current.expandedSections, sectionId],
        };
      });
    },
    [updatePrefs]
  );

  const setExpandedSections = useCallback(
    (sectionIds: SectionId[]) => {
      updatePrefs(current => ({ ...current, expandedSections: sectionIds }));
    },
    [updatePrefs]
  );

  const togglePinWidget = useCallback(
    (widgetId: string) => {
      updatePrefs(current => {
        const isPinned = current.pinnedWidgets.includes(widgetId);
        return {
          ...current,
          pinnedWidgets: isPinned
            ? current.pinnedWidgets.filter(id => id !== widgetId)
            : [...current.pinnedWidgets, widgetId],
        };
      });
    },
    [updatePrefs]
  );

  const togglePinSection = useCallback(
    (sectionId: SectionId) => {
      updatePrefs(current => {
        const currentPinned = current.pinnedSections ?? [];
        const isPinned = currentPinned.includes(sectionId);
        return {
          ...current,
          pinnedSections: isPinned
            ? currentPinned.filter(id => id !== sectionId)
            : [...currentPinned, sectionId],
        };
      });
    },
    [updatePrefs]
  );

  const moveSection = useCallback(
    (sectionId: SectionId, direction: 'up' | 'down') => {
      updatePrefs(current => {
        const currentOrder = current.sectionOrder ?? [...DEFAULT_SECTION_ORDER];
        const index = currentOrder.indexOf(sectionId);

        if (index === -1) return current;
        if (direction === 'up' && index === 0) return current;
        if (direction === 'down' && index === currentOrder.length - 1) return current;

        const newOrder = [...currentOrder];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        const swapItem = newOrder[swapIndex];
        if (swapItem !== undefined) {
          newOrder[swapIndex] = sectionId;
          newOrder[index] = swapItem;
        }

        return { ...current, sectionOrder: newOrder };
      });
    },
    [updatePrefs]
  );

  const resetToDefaults = useCallback(() => {
    if (!rolePreset) return;

    const defaultPrefs: UserDashboardPreferences = {
      expandedSections: rolePreset.expandedSections,
      pinnedWidgets: rolePreset.pinnedWidgets,
      pinnedSections: [],
      collapsedSections: [],
    };

    setLocalPrefs(defaultPrefs);
    // Delete all category entries + re-set defaults
    deletePreferences('dashboard-widgets')
      .then(() => {
        return bulkSetPreferences('dashboard-widgets', prefsToEntries(defaultPrefs));
      })
      .then(() => queryClient.invalidateQueries({ queryKey: QUERY_KEY }))
      .catch((err: unknown) => {
        console.warn('[useDashboardPreferences] resetToDefaults failed:', err);
      });
  }, [rolePreset, queryClient]);

  // Single-key persist (for future granular updates — currently unused but wired)
  void persistKey;

  // ---------------------------------------------------------------------------
  // Computed helpers
  // ---------------------------------------------------------------------------

  const isSectionExpanded = useCallback(
    (sectionId: SectionId) => expandedSections.includes(sectionId),
    [expandedSections]
  );

  const isWidgetPinned = useCallback(
    (widgetId: string) => pinnedWidgets.includes(widgetId),
    [pinnedWidgets]
  );

  const isSectionPinned = useCallback(
    (sectionId: SectionId) => pinnedSections.includes(sectionId),
    [pinnedSections]
  );

  const canMoveUp = useCallback(
    (sectionId: SectionId) => sectionOrder.indexOf(sectionId) > 0,
    [sectionOrder]
  );

  const canMoveDown = useCallback(
    (sectionId: SectionId) => {
      const index = sectionOrder.indexOf(sectionId);
      return index >= 0 && index < sectionOrder.length - 1;
    },
    [sectionOrder]
  );

  return {
    expandedSections,
    pinnedWidgets,
    pinnedSections,
    sectionOrder,
    isLoading: authLoading || dbLoading,
    toggleSection,
    setExpandedSections,
    togglePinWidget,
    togglePinSection,
    moveSection,
    resetToDefaults,
    isSectionExpanded,
    isWidgetPinned,
    isSectionPinned,
    canMoveUp,
    canMoveDown,
  };
}
